<script setup>
import { ref, watch, onMounted, onBeforeUnmount } from "vue";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import "leaflet-draw";
import "leaflet-draw/dist/leaflet.draw.css";

const emit = defineEmits(["draw-created", "draw-edited", "draw-deleted", "map-center-changed"]);

const props = defineProps({
  center: { type: Array, default: () => [53.41291, -8.24389] }, // Center of Ireland (changed from Limerick)
  zoom: { type: Number, default: 8 }, // Reduced zoom to show more of Ireland
  geojson: { type: Object, default: null },
});

const mapContainer = ref(null);
let map, drawnItems, drawControl;
let isUpdatingProgrammatically = false; // Flag to prevent recursive updates
let moveendHandler = null; // Handler function for moveend event

onMounted(() => {
  // 1) init
  map = L.map(mapContainer.value, {
    center: props.center,
    zoom: props.zoom,
    zoomControl: true,
    attributionControl: false,
  });
  L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
    maxZoom: 19,
    attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
  }).addTo(map);

  // 2) one FeatureGroup for both draw & edit
  drawnItems = new L.FeatureGroup();
  map.addLayer(drawnItems);

  // 3) draw toolbar
  drawControl = new L.Control.Draw({
    edit: { featureGroup: drawnItems, remove: true },
    draw: {
      polygon: true,
      rectangle: true,
      polyline: true,
      circle: false,
      marker: false,
      circlemarker: false,
    },
  });
  map.addControl(drawControl);

  // 4) load initial geojson into drawnItems
  loadGeojson(props.geojson);

  // 5) wire events
  map.on(L.Draw.Event.CREATED, (e) => {
    drawnItems.addLayer(e.layer);
    emit("draw-created", e.layer.toGeoJSON());
  });
  map.on(L.Draw.Event.EDITED, (e) => {
    e.layers.eachLayer((l) => {
      /* no-op */
    });
    emit("draw-edited", drawnItems.toGeoJSON());
  });
  map.on(L.Draw.Event.DELETED, (e) => {
    e.layers.eachLayer((l) => drawnItems.removeLayer(l));
    emit("draw-deleted", drawnItems.toGeoJSON());
  });

  // 6) Capture map center when user pans/zooms (but only if no geometry is drawn)
  // Store the handler function so we can remove/re-add it
  moveendHandler = () => {
    if (!map || !drawnItems) return;
    // Don't emit if we're programmatically updating (prevents recursive loop)
    if (isUpdatingProgrammatically) return;
    // CRITICAL: Don't emit if geometry exists - geometry controls the view
    if (props.geojson || (drawnItems && drawnItems.getLayers().length > 0)) {
      return; // Geometry exists, don't emit center changes
    }
    // Only capture center if no geometry has been drawn yet
    const center = map.getCenter();
    const zoom = map.getZoom();
    emit('map-center-changed', {
      center: [center.lat, center.lng],
      zoom: zoom
    });
  };
  map.on('moveend', moveendHandler);
});

// re-load whenever parent geojson changes
watch(
  () => props.geojson,
  (newVal) => {
    // When geojson loads, it will fit bounds - prevent center watch from interfering
    loadGeojson(newVal);
  },
  { immediate: false }
);

// Watch center changes and update map (but only if no geometry is drawn)
// Track last center to avoid unnecessary updates
let lastCenter = null;

watch(
  () => props.center,
  (newCenter) => {
    if (!map || !newCenter || !Array.isArray(newCenter) || newCenter.length !== 2) return;

    // Check if center actually changed (deep comparison to avoid unnecessary updates)
    if (lastCenter &&
        lastCenter[0] === newCenter[0] &&
        lastCenter[1] === newCenter[1]) {
      return; // No actual change, skip update
    }
    lastCenter = [...newCenter]; // Store copy

    // Don't update map center if geometry already exists (check both drawnItems and geojson prop)
    // IMPORTANT: Check props.geojson FIRST because it's set before drawnItems has layers
    const hasGeometry = props.geojson || (drawnItems && drawnItems.getLayers().length > 0);
    if (hasGeometry) {
      // If geometry exists, don't override - geometry controls the view via fitBounds
      // The loadGeojson function will handle fitting bounds
      return;
    }

    // Set flag to prevent moveend from emitting during programmatic update
    isUpdatingProgrammatically = true;

    // Temporarily remove moveend listener, update view, then re-add
    map.off('moveend', moveendHandler);
    map.setView(newCenter, map.getZoom());

    // Re-add moveend listener after a short delay
    setTimeout(() => {
      map.on('moveend', moveendHandler);
      isUpdatingProgrammatically = false;
    }, 100);
  }
);

// helper to clear & re-add into drawnItems
function loadGeojson(geojson) {
  if (!map || !drawnItems) return;
  drawnItems.clearLayers();
  if (geojson) {
    L.geoJSON(geojson, {
      style: (f) => ({
        color: f.properties?.colour || "#f00",
        weight: 3,
        fillOpacity: 0.2,
      }),
    }).eachLayer((l) => drawnItems.addLayer(l));
    // zoom to fit - temporarily disable center watch to prevent override
    const b = drawnItems.getBounds();
    if (b.isValid()) {
      isUpdatingProgrammatically = true;
      if (moveendHandler) {
        map.off('moveend', moveendHandler);
      }
      map.fitBounds(b, { padding: [20, 20] });
      // Re-enable after fitBounds completes
      setTimeout(() => {
        if (moveendHandler) {
          map.on('moveend', moveendHandler);
        }
        isUpdatingProgrammatically = false;
      }, 200);
    }
  }
}

onBeforeUnmount(() => {
  map?.remove();
  map = null;
});
</script>

<template>
  <div class="map-wrapper">
    <div ref="mapContainer" class="map-container"></div>
  </div>
</template>

<style scoped>
.map-wrapper {
  width: 100%;
  height: 100%;
}
.map-container {
  width: 100%;
  height: 100%;
}
</style>
